import React, { PropsWithChildren, createContext, useContext, useState, useEffect } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride'
import { useHistory, useLocation } from 'react-router-dom'

import {
  ICustomCallBackProps,
  IOnboardingCustomJoyrideStep,
  IOnboardingJoyrideStep,
  ONBOARDING_JOYRIDE_STEP_TYPE,
} from './onboardingJoyride.types'
import { generateJoyrideSteps } from './onboardingJoyride.utils'
import { BaseJoyrideTooltip } from './tooltip/BaseJoyrideTooltip'

export interface IOnboardingJoyrideProps {
  autostartLocation?: string
  steps?: (t: TFunction<'translation', undefined>) => IOnboardingCustomJoyrideStep[]
}

export interface IOnboardingJoyrideContext {
  isJoyrideRunning: boolean
  pauseJoyride: () => void
  resumeJoyride: () => void
  startJoyride: () => void
  steps?: IOnboardingJoyrideStep[]
}

const OnboardinJoyrideContext = createContext<IOnboardingJoyrideContext | undefined>(undefined)

export function useOnboardingJoyride() {
  const ctx = useContext(OnboardinJoyrideContext)

  if (!ctx) {
    throw new Error('useOnboardingJoyride must be use inside OnboardingJoyrideWrapper')
  }

  return ctx
}

export default function OnboardingJoyrideWrapper({
  steps: inSteps,
  autostartLocation,
  children,
}: PropsWithChildren<IOnboardingJoyrideProps>) {
  const { t } = useTranslation()

  const [steps, setSteps] = useState<IOnboardingJoyrideStep[]>([])
  const [stepIndex, setStepIndex] = useState(0)
  const [touring, setTouring] = useState(false)
  const [running, setRunning] = useState(false)

  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    if (inSteps) {
      setSteps(generateJoyrideSteps(inSteps(t)))
    } else {
      setSteps(
        generateJoyrideSteps([
          {
            type: ONBOARDING_JOYRIDE_STEP_TYPE.GENERAL,
            content: 'No steps defined',
          },
        ] as IOnboardingCustomJoyrideStep[])
      )
    }
    return () => {
      setTouring(false)
      setRunning(false)
      setStepIndex(0)
    }
  }, [t, inSteps])

  useEffect(() => {
    if (!!autostartLocation) {
      if (location.pathname.includes(autostartLocation)) {
        startJoyride()
      }
    }
  }, [location.pathname])

  useEffect(() => {
    // If Step doesn't require to wait (loading data etc...) we resume the Joyride
    const isWaiting = touring && steps?.[stepIndex].wait
    if (!isWaiting) {
      resumeJoyride()
    }
  }, [location])

  const startJoyride = () => {
    if (steps) {
      setStepIndex(0)
      setRunning(true)
      setTouring(true)
    }
  }

  const pauseJoyride = () => {
    if (touring === true) {
      setRunning(false)
    }
  }

  const resumeJoyride = () => {
    if (touring === true) {
      setRunning(true)
    }
  }

  const handleStep = ({ type, status, action, index, step }: ICustomCallBackProps) => {
    const redirection = step.redirection

    if (index === 0) {
      setTouring(true)
    }

    if ([EVENTS.TOUR_START].includes(type as any)) {
      if (redirection) {
        // Redirect before playing the step
        if (redirection?.origin && redirection.origin !== location.pathname) {
          setRunning(false)
          history.push(redirection.origin)
          return
        }
      }
    }

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type as any)) {
      // Set step index before rendering to avoid playing #root steps
      setStepIndex((index) => Math.max(index + (action === ACTIONS.PREV ? -1 : 1), 0))

      if (!!step?.wait && step.wait === true) {
        pauseJoyride()
      }

      if (redirection) {
        // Redirect after the surrent step
        if (redirection?.next && redirection.next !== location.pathname) {
          setRunning(false)
          history.push(redirection.next)
          return
        }
      }
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
      setRunning(false)
      setTouring(false)
    }
  }

  const handleCallback = (props: ICustomCallBackProps) => {
    return handleStep(props)
  }

  const getCtxValue = () => {
    return {
      steps,
      isJoyrideRunning: running,
      startJoyride,
      pauseJoyride,
      resumeJoyride,
    }
  }

  return (
    <OnboardinJoyrideContext.Provider value={getCtxValue()}>
      <>
        {children}
        <Joyride
          callback={(data) => handleCallback(data as ICustomCallBackProps)}
          continuous
          run={running}
          steps={steps}
          stepIndex={stepIndex}
          styles={{}}
          tooltipComponent={({ step, ...props }) => (
            <BaseJoyrideTooltip step={step as IOnboardingCustomJoyrideStep} {...props} />
          )}
        />
      </>
    </OnboardinJoyrideContext.Provider>
  )
}
